import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Composant d'image optimisé avec support WebP, srcset et lazy loading
 * 
 * Features:
 * - Format WebP avec fallback PNG
 * - Images responsives avec srcset
 * - Lazy loading natif
 * - Placeholder pendant le chargement
 * - Gestion des erreurs
 * 
 * @example
 * <OptimizedImage
 *   src={publishImage}
 *   alt="Publish Listings"
 *   sizes="(max-width: 480px) 40px, (max-width: 768px) 48px, 70px"
 *   className="feature-image"
 * />
 */
const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  sizes = '(max-width: 480px) 40px, (max-width: 768px) 48px, 192px',
  loading = 'lazy',
  style = {},
  onLoad,
  onError
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Extraire le nom de fichier et l'extension
  const getImageInfo = (imagePath) => {
    if (typeof imagePath !== 'string') {
      return { base: '', dir: '', name: '', ext: '' };
    }
    
    const lastSlash = imagePath.lastIndexOf('/');
    const dir = lastSlash >= 0 ? imagePath.substring(0, lastSlash) : '';
    const filename = lastSlash >= 0 ? imagePath.substring(lastSlash + 1) : imagePath;
    const lastDot = filename.lastIndexOf('.');
    const name = lastDot >= 0 ? filename.substring(0, lastDot) : filename;
    const ext = lastDot >= 0 ? filename.substring(lastDot) : '';
    
    return { base: imagePath, dir, name, ext };
  };

  const imageInfo = getImageInfo(src);

  // Générer les sources srcset pour WebP et PNG
  const generateSrcSet = (format) => {
    if (!imageInfo.name) return '';
    
    const extension = format === 'webp' ? '.webp' : '.png';
    const sources = [
      `${imageInfo.dir}/${imageInfo.name}-sm${extension} 80w`,
      `${imageInfo.dir}/${imageInfo.name}-md${extension} 160w`,
      `${imageInfo.dir}/${imageInfo.name}-lg${extension} 320w`
    ];
    
    return sources.join(', ');
  };

  const handleLoad = (e) => {
    setIsLoaded(true);
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    setImageError(true);
    if (onError) onError(e);
  };

  // Réinitialiser l'état si src change
  useEffect(() => {
    setImageError(false);
    setIsLoaded(false);
  }, [src]);

  // Si erreur, utiliser l'image originale en fallback
  if (imageError) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        decoding="async"
      />
    );
  }

  return (
    <picture>
      {/* Source WebP avec srcset */}
      <source
        type="image/webp"
        srcSet={generateSrcSet('webp')}
        sizes={sizes}
      />
      
      {/* Source PNG avec srcset (fallback) */}
      <source
        type="image/png"
        srcSet={generateSrcSet('png')}
        sizes={sizes}
      />
      
      {/* Image par défaut avec lazy loading */}
      <img
        src={`${imageInfo.dir}/${imageInfo.name}-md.png`}
        alt={alt}
        className={`${className} ${!isLoaded ? 'loading' : 'loaded'}`}
        style={{
          ...style,
          opacity: isLoaded ? 1 : 0.5,
          transition: 'opacity 0.3s ease-in-out'
        }}
        loading={loading}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
      />
    </picture>
  );
};

OptimizedImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  sizes: PropTypes.string,
  loading: PropTypes.oneOf(['lazy', 'eager']),
  style: PropTypes.object,
  onLoad: PropTypes.func,
  onError: PropTypes.func
};

export default OptimizedImage;
